import { z } from 'zod';

import { PermissionRoleSchema } from './common.js';
import { FilterSchema } from './filters.js';
import { GlobalColorSchema, WidgetSchema } from './widgets.js';

const DashboardFiltersSchema = z
  .object({
    filters: z.array(FilterSchema).optional(),
  })
  .catchall(z.unknown());

const DashboardTemplateSchema = z
  .object({
    widgets: z.array(WidgetSchema).optional(),
    settings: DashboardFiltersSchema.optional(),
    version: z.string().optional(),
    boardZoom: z.number().optional(),
  })
  .catchall(z.unknown());

export const DashboardSchema = z.object({
  id: z.string(),
  name: z.string(),
  companyId: z.coerce.string(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  configuration: DashboardTemplateSchema,
  publicAccess: PermissionRoleSchema,
  userAccess: PermissionRoleSchema,
  ownerId: z.string(),
});

const DashboardsResponseDataSchema = z.object({
  items: z.array(DashboardSchema),
  totalItems: z.number().optional(),
  message: z.string().nullable().optional(),
});

export const DashboardsResponseSchema = z.object({
  data: DashboardsResponseDataSchema,
  error: z.unknown().nullable().optional(),
});

export const CreateDashboardRequestSchema = z.object({
  type: z.literal('dashboard'),
  name: z.string(),
  configuration: DashboardTemplateSchema,
});

export const UpdateDashboardRequestSchema = z
  .object({
    name: z.string().optional(),
    configuration: DashboardTemplateSchema.optional(),
  })
  .refine((data) => Boolean(data.name) || Boolean(data.configuration), {
    message: 'Either name or configuration must be provided.',
  });

export const DashboardTypeQuerySchema = z.object({
  type: z.literal('dashboard'),
});

export const PivotTypeQuerySchema = z.object({
  type: z.literal('pivot'),
});

export const DeleteDashboardResponseSchema = z.object({}).catchall(z.unknown());

export const DashboardsListSettingsSchema = z.object({
  id: z.string(),
  userId: z.string(),
  viewType: z.enum(['pivot', 'dashboard']),
  settings: z.object({
    ordering: z.array(z.string()),
    colors: z.array(GlobalColorSchema),
  }),
});

export const DashboardsListSettingsResponseSchema = z.object({
  data: z.object({
    items: z.array(DashboardsListSettingsSchema),
  }),
  error: z.unknown().nullable().optional(),
});

export const PivotListSettingsSchema = z.object({
  id: z.string(),
  userId: z.string(),
  viewType: z.literal('pivot'),
  settings: z.object({
    ordering: z.array(z.string()),
  }),
});

export const PivotListSettingsResponseSchema = z.object({
  data: z.object({
    items: z.array(PivotListSettingsSchema),
  }),
  error: z.unknown().nullable().optional(),
});

export const DashboardSettingsParamsSchema = z.object({
  ordering: z.array(z.string()),
  colors: z.array(GlobalColorSchema),
});

export const CreateDashboardsListSettingsRequestSchema = z.object({
  viewType: z.literal('dashboard'),
  settings: DashboardSettingsParamsSchema,
});

export const PivotSettingsParamsSchema = z.object({
  ordering: z.array(z.string()),
});

export const CreatePivotListSettingsRequestSchema = z.object({
  viewType: z.literal('pivot'),
  settings: PivotSettingsParamsSchema,
});

export const DeleteDashboardsListSettingsResponseSchema = z
  .object({})
  .catchall(z.unknown());

export const ViewTypeSchema = z.enum(['pivot', 'dashboard', 'pivot_reach']);

export const ViewSchema = DashboardSchema.extend({
  viewType: ViewTypeSchema,
});

const ViewsResponseDataSchema = z.object({
  items: z.array(ViewSchema),
  totalItems: z.number().optional(),
});

export const ViewsResponseSchema = z.object({
  data: ViewsResponseDataSchema,
  error: z.unknown().nullable().optional(),
});

export const ViewsQuerySchema = z.object({
  type: z.union([ViewTypeSchema, z.array(ViewTypeSchema)]).optional(),
});

export const ViewQuerySchema = z.object({
  type: ViewTypeSchema.optional(),
});

export const DeleteViewResponseSchema = z.object({}).catchall(z.unknown());

export const CreateViewRequestSchema = z.object({
  type: ViewTypeSchema,
  name: z.string(),
  configuration: DashboardTemplateSchema,
});

export const UpdateViewRequestSchema = z
  .object({
    name: z.string().optional(),
    configuration: DashboardTemplateSchema.optional(),
  })
  .refine((data) => Boolean(data.name) || Boolean(data.configuration), {
    message: 'Either name or configuration must be provided.',
  });

export type ViewType = z.infer<typeof ViewTypeSchema>;
export type View = z.infer<typeof ViewSchema>;
export type CreateViewRequest = z.infer<typeof CreateViewRequestSchema>;
export type UpdateViewRequest = z.infer<typeof UpdateViewRequestSchema>;

// Sharing schemas

const ApiSharedUserSchema = z.object({
  role: PermissionRoleSchema,
  userId: z.coerce.string(),
});

const ApiSharedUsersItemSchema = z.object({
  viewId: z.string(),
  sharedWith: z.array(ApiSharedUserSchema),
  publicAccess: PermissionRoleSchema,
});

const SharedUsersResponseDataSchema = z.object({
  items: z.array(ApiSharedUsersItemSchema),
});

export const SharedUsersResponseSchema = z.object({
  data: SharedUsersResponseDataSchema,
  error: z.unknown().nullable().optional(),
});

export const UpdatePermissionRequestSchema = z.object({
  userAccess: z.array(ApiSharedUserSchema),
  publicAccess: PermissionRoleSchema,
});

// Client-facing types (converted from API format)
export const SharedUserSchema = z.object({
  id: z.string(),
  role: PermissionRoleSchema,
});

// API response type (raw format from server)
type ApiSharedUsersResponse = z.infer<typeof SharedUsersResponseSchema>;

// Client-facing types (converted from API format)
export type SharedUser = z.infer<typeof SharedUserSchema>;
export type GetSharedUsersResponse = {
  id: string;
  sharedWith: SharedUser[];
  publicAccess: z.infer<typeof PermissionRoleSchema>;
};
export type UpdatePermissionRequest = z.infer<
  typeof UpdatePermissionRequestSchema
>;

// Helper to convert API response to client format
export const convertSharedUsersResponse = (
  response: ApiSharedUsersResponse,
): GetSharedUsersResponse => {
  const item = response.data.items[0];
  if (!item) {
    throw new Error('No shared users data found');
  }

  return {
    id: item.viewId,
    sharedWith: item.sharedWith.map(({ role, userId }) => ({
      id: userId,
      role,
    })),
    publicAccess: item.publicAccess,
  };
};
